import { FormApi } from 'final-form';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Box } from '@mui/material';
import { useCallback } from 'react';

import { AwardType, Flow_Types } from '@occc-app/offerHelpers/types';
import { Offer_Engagement_Type, OfferFilter } from '@occc/graphql';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import { LoadingOverlay } from '@occc-app/design-system';
import { AwardCount } from '@occc-app/routes/CampaignComponents/components/AwardCount';
import { CustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/CustomData';
import { configureFormCustomDataSubtitle } from '@occc-app/offerHelpers/copies';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { VoucherExpirationDaysRedemption } from '@occc-app/routes/CampaignComponents/VoucherExpirationDaysRedemption';
import {
  AwardRestrictions,
  AwardRestrictionsAwardTypeWatcher,
  FulfillmentWindow,
} from '@occc/feat-constraints';

import { validateConfiguration } from './promocodes-configuration.schema';
import {
  PromocodesConfigureFormValues,
  PromocodesConfigureFormInitialValues,
} from './promocodes-configuration.types';
import { PromocodesConfiguration } from './promocodes-configuration';

type ConfigureFormProps = {
  onSubmit: (
    values: PromocodesConfigureFormValues,
    form: FormApi<PromocodesConfigureFormValues, PromocodesConfigureFormInitialValues>,
  ) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: PromocodesConfigureFormInitialValues;
  loading: boolean;
  currentFlowType: Flow_Types;
  makeOffersFilter: (search: string) => OfferFilter;
  isFormEditable: boolean;
  onNextStepButtonClick?: () => void;
};

export const PromocodesConfigurationFrom = ({
  onSubmit,
  initialValues,
  onCancelClick,
  loading,
  currentFlowType,
  makeOffersFilter,
  isFormEditable,
  onNextStepButtonClick,
}: ConfigureFormProps) => {
  const renderForm = useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      dirty,
      form,
    }: FormRenderProps<PromocodesConfigureFormValues, PromocodesConfigureFormInitialValues>) => {
      const isAwardRestrictionsShown = values.promocodes.awardType === AwardType.AwardVouchers;
      const isVoucherRedemptionShown = values.promocodes.awardType === AwardType.AwardVouchers;
      const awardVouchers =
        values.promocodes.awardType === AwardType.AwardVouchers
          ? values.promocodes.awardVouchers
          : undefined;
      const isVoucherRedemptionApplicable =
        awardVouchers?.voucheredOffer?.engagementType === Offer_Engagement_Type.Vouchered;
      const isVoucherRedemptionEnabled = awardVouchers?.isEnabled ?? false;

      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={handleCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
          <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
            <OfferEarningMechanismConfigurationSection>
              <Box mb={2}>
                <OfferNameAndDescriptionConfiguration entityName='Promo Code Offer' />
              </Box>
              <PromocodesConfiguration values={values} makeOffersFilter={makeOffersFilter} />
            </OfferEarningMechanismConfigurationSection>

            <OfferConfigurationSection title='Limits'>
              {isVoucherRedemptionShown && (
                <VoucherExpirationDaysRedemption
                  isVoucherRedemptionEnabled={isVoucherRedemptionEnabled}
                  isVoucherRedemptionApplicable={isVoucherRedemptionApplicable}
                  showAvatar
                  fieldNamePrefix='promocodes'
                />
              )}
              <AwardCount values={values} />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Restrictions'>
              <FulfillmentWindow values={values} />
              {isAwardRestrictionsShown && <AwardRestrictions />}
              <AwardRestrictionsAwardTypeWatcher
                awardTypeFieldName='promocodes.awardType'
                form={form}
              />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Advanced Offer Setting'>
              <CustomData values={values.customData} subtitle={configureFormCustomDataSubtitle} />
            </OfferConfigurationSection>
          </OfferConfigurationWizardFormLayout>
        </OfferForm>
      );
    },
    [isFormEditable, currentFlowType, onNextStepButtonClick, makeOffersFilter, onCancelClick],
  );
  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<PromocodesConfigureFormValues, PromocodesConfigureFormInitialValues>
          onSubmit={onSubmit}
          render={renderForm}
          mutators={{
            ...arrayMutators,
            setFieldValue: (args, state, utils) => {
              utils.changeValue(state, args[0], () => args[1]);
            },
          }}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
